import { Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, InputBase, Link, List, ListItem, ListItemText, Paper, Typography, Zoom } from "@material-ui/core";
import { Check, Person, PersonAdd } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { Link as RouterLink } from 'react-router-dom';

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { InviteOrgUserModal } from "components/InviteOrgUserModal";
import { useOrg } from "components/OrgProvider";
import { useOrgUserInvites } from "components/OrgUserInvitesProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { withdrawInvite } from "modelActions/OrgUserInvite";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser, noAppUserNameText } from "models/AppUser";
import { noEmailText } from "models/Email";
import { Grant } from "models/Grant";
import { OrgRoles } from "models/OrgRoles";
import { getOrgUserGrants, OrgUser, orgUserIdFrom } from "models/OrgUser";
import { AppUserRolePicker } from "MyAppUserScreen";
import { Paths } from "Paths";
import { dateFormat } from "Util/date.utils";

import { EmptyPage } from "../EmptyPage";
import { DeleteOrgUserModal } from "./OrgUserScreen";

type Props = {
    id?: string
}

type ModalProps = {
    children: React.ReactNode
    title: string
    onClose: () => void
}

function WarningModal({ onClose, title, children }: ModalProps) {
    return (
        <Dialog
            open
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {children}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" autoFocus>Ok</Button>
            </DialogActions>
        </Dialog>
    )
}

export function AppUserScreen({ id }: Props) {
    const { enqueueSnackbar } = useSnackbar()
    const [invites, loadingInvites] = useOrgUserInvites()
    const [ownAppUser, loadingOwnAppUser] = useAppUser()
    const [org, loadingOrg] = useOrg()
    const [showDeleteUser, setShowDeleteUser] = useState(false)
    const [appUserDoc, loadingAppUser] = FirebaseUtils.useDocumentData<AppUser>(
        true,
        Collections.APP_USERS,
        id || null,
        AnalyticsEvent.GET_APP_USER_ERROR,
        {},
    )
    const appUser = appUserDoc?.data || null
    const [orgUser, loadingOrgUser] = FirebaseUtils.useDocumentData<OrgUser>(
        true,
        Collections.ORG_USERS,
        org && appUser && orgUserIdFrom(org?.data.id, appUser?.id),
        AnalyticsEvent.FETCH_ORG_USER_ERROR
    )

    const [ownOrgUser, loadingOwnOrgUser] = useOrgUser()


    const [ownerRoleWarning, setOwnerRoleWarning] = useState('')
    const [showInviteUser, setShowInviteUser] = useState(false)

    const loading = loadingAppUser || loadingOwnAppUser || loadingOrg || loadingOrgUser || loadingOwnOrgUser || loadingInvites

    const isOwnUser = appUser?.id === ownAppUser?.data.id

    const grants = getOrgUserGrants(ownOrgUser?.data || null)
    const canInvite = !!grants[Grant.INVITE_USER]

    const activeInvite = appUser && invites?.find(invite => invite.data.invitedUser.appUser.id === appUser.id)

    const isOrgOwner = orgUser?.data.userId === org?.data.owner.appUser?.id

    if (!loading && !appUser) {
        return <EmptyPage text="Fant ikke App Brukeren" />
    }

    const content = loading ? <FullscreenLoading screenId="App User" /> : (
        (!appUser || !appUserDoc) ? (
            <List>
                <ListItem>
                    <ListItemText primary="Kunne ikke finne brukeren" />
                </ListItem>
            </List>
        ) : (
            <List>

                {(canInvite && showInviteUser && appUser && ownAppUser && org && ownOrgUser && appUser && !orgUser && !loadingOrgUser) ? (
                    <InviteOrgUserModal
                        org={org.data}
                        orgUser={ownOrgUser.data}
                        appUser={ownAppUser.data}
                        inviteUser={appUser}
                        setInviteUser={() => setShowInviteUser(false)}
                    />
                ) : null}
                <ListItem>
                    <Box mx="auto" mt="40px">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Paper
                                elevation={3}
                                style={{
                                    position: 'relative',
                                    //width: 300,
                                    //height: 300,
                                    borderRadius: 18,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Avatar
                                    style={{ width: 120, height: 120, marginTop: -40 }}
                                    alt="Org Logo"
                                    src={appUser.avatarImage?.original.url || undefined}
                                >
                                    <Person />
                                </Avatar>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        marginTop: 18,
                                        marginBottom: 18,
                                    }}
                                >
                                    <InputBase
                                        value={noAppUserNameText(appUser.contactInfo?.name?.fullName)}
                                        disabled
                                        inputProps={{
                                            'aria-label': 'app user name input',
                                            style: {
                                                fontSize: 26,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />

                                    <Typography variant="h6">
                                        {appUser.contactInfo?.email?.email ? (
                                            <Link href={`mailto:${appUser.contactInfo?.email?.email}`}>
                                                {appUser.contactInfo?.email?.email}
                                            </Link>
                                        ) : noEmailText(appUser.contactInfo?.email?.email)}
                                    </Typography>
                                    <Typography variant="h6">
                                        {appUser.contactInfo?.phone?.phoneNumberString ? (
                                            <Link href={`tel:${appUser.contactInfo?.phone?.phoneNumberString}`}>
                                                {appUser.contactInfo?.phone?.phoneNumberString}
                                            </Link>
                                        ) : 'Ingen tlf'}
                                    </Typography>
                                    <Typography variant="subtitle2">Opprettet: {dateFormat.format(appUserDoc.metadata.createdAt.toDate())}</Typography>
                                    {isOwnUser ? (
                                        <Box mt={2}>
                                            <Link component={RouterLink} to={Paths.MY_APP_USER}>Gå til min App bruker</Link>
                                        </Box>
                                    ) : null}
                                    {orgUser ? (
                                        <Box mt={2}>
                                            {isOwnUser ? (
                                                <Link component={RouterLink} to={Paths.MY_ORG_USER}>Gå til min Org bruker</Link>
                                            ) : (
                                                <Link component={RouterLink} to={Paths.ORG_USERS + '/' + orgUser.data.id}>Gå til Org bruker</Link>
                                            )}
                                        </Box>
                                    ) : null}
                                </div>
                                <Zoom in>
                                    <div>
                                        {(!isOwnUser && canInvite && ownOrgUser && ownAppUser && appUser && !orgUser && !activeInvite && !loadingOrgUser) ? (
                                            <Button
                                                size="medium"
                                                style={{ marginBottom: -20 }}
                                                onClick={() => setShowInviteUser(true)}
                                                color="primary"
                                                aria-label="invite"
                                                variant="contained"
                                                endIcon={<PersonAdd />}
                                            >
                                                Inviter bruker
                                            </Button>
                                        ) : null}
                                        {!!orgUser ? (
                                            <Chip
                                                style={{ marginBottom: -20 }}
                                                label="I orgen"
                                                icon={<Check />}
                                                color="primary"
                                                onDelete={(!isOwnUser && !isOrgOwner && ownOrgUser?.data.orgRoles.map[OrgRoles.FlatEnum.OWNER]) ? () => setShowDeleteUser(true) : undefined}
                                            />
                                        ) : (
                                            (activeInvite && ownOrgUser && ownAppUser) ? (
                                                <Chip
                                                    style={{ marginBottom: -20 }}
                                                    label="Invitert"
                                                    color="secondary"
                                                    onDelete={canInvite ? () => {
                                                        withdrawInvite(ownOrgUser.data, ownAppUser.data, activeInvite.data, enqueueSnackbar)
                                                    } : undefined}
                                                />
                                            ) : null
                                        )}
                                    </div>
                                </Zoom>
                            </Paper>
                        </div>
                    </Box>
                </ListItem>
                {(!loading && ownAppUser) ? <AppUserRolePicker appUser={appUser} ownAppUser={ownAppUser.data} isOwnUser={isOwnUser} /> : null}
            </List>
        )
    )

    return (
        <PageNavigation title="App Bruker" backHistoryButton>
            {ownerRoleWarning ? (
                <WarningModal title="Kan ikke fjerne rolle" onClose={() => setOwnerRoleWarning('')}>
                    <Typography variant="subtitle1">{ownerRoleWarning}</Typography>
                </WarningModal>
            ) : null}
            {(showDeleteUser && ownAppUser && ownOrgUser && orgUser) ? (
                <DeleteOrgUserModal
                    orgUser={orgUser.data}
                    ownAppUser={ownAppUser.data}
                    ownOrgUser={ownOrgUser.data}
                    close={() => setShowDeleteUser(false)}
                    isOwnUser={isOwnUser}
                    isOrgOwner={isOrgOwner}
                />
            ) : null}
            {content}
        </PageNavigation>
    )
}